import React, { useEffect, useState } from "react";
import Title from "antd/es/typography/Title";
import Paragraph from "antd/es/typography/Paragraph";
import { Alert, Button } from "antd";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomForm from "../../../Components/Form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import "./index.scss";
import BackContinue from "../../../Components/Form/BackContinue";
import logoText from "../../../Assets/Icons/logo_text.svg";
import {
  forgotFields,
  resetPasswordFields,
} from "../../../Utils/FormikFieldSkeleton";
import {
  confirmNewPassword,
  resetErrorMessage,
  sendResetCode,
} from "../../../Utils/passwordReset";

const ForgotPassword = () => {
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const isForgot = pathname === "/forgot-password";
  const email = state?.userName;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resent, setResent] = useState(false);

  // * Both routes render this component, so it is kept between them; clear
  // * what the previous step left behind. * //
  useEffect(() => {
    setError(null);
    setResent(false);
  }, [pathname]);

  // * Reached without an email (a bookmark, a shared link): start again. * //
  if (!isForgot && !email) return <Navigate to="/forgot-password" replace />;

  const run = async (task) => {
    setLoading(true);
    setError(null);
    try {
      await task();
    } catch (err) {
      setError(resetErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // * Only moves on once Cognito has sent the code, so the next screen's
  // * "we've sent you a code" is true. * //
  const onFinish = (values) =>
    isForgot
      ? run(async () => {
          const address = values.workEmail.trim();
          await sendResetCode(address);
          navigate("/reset-password", { state: { userName: address } });
        })
      : run(async () => {
          await confirmNewPassword(email, values.code, values.password);
          toast.success("Password updated. Sign in with your new password.");
          setTimeout(() => navigate("/"), 2000);
        });

  const resendCode = () =>
    run(async () => {
      setResent(false);
      await sendResetCode(email);
      setResent(true);
    });

  return (
    <div className="forgot-password-container">
      <img src={logoText} width="201px" alt="logo" />
      <Title level={1} className="font-medium text-black">
        Recover your account
      </Title>
      {isForgot ? (
        <Paragraph>
          Enter the email associated with your account, and we'll send you a 6
          digit verification code
        </Paragraph>
      ) : (
        <>
          <Paragraph className="reset-margin">
            We’ve sent a 6 digit verification code to
          </Paragraph>
          <Paragraph className="text-primary reset-margin">
            {email}
          </Paragraph>
          <Paragraph>
            It can take a minute to arrive. If you don't see it, check your spam
            folder.
          </Paragraph>
        </>
      )}
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginTop: "16px" }}
        />
      )}
      {resent && !error && (
        <Alert
          type="success"
          showIcon
          message="We've sent a new code. Use the one in the newest email."
          style={{ marginTop: "16px" }}
        />
      )}
      <CustomForm
        fields={isForgot ? forgotFields : resetPasswordFields}
        onFinish={onFinish}
        style={{ marginTop: "40px" }}
        FormBtn={BackContinue}
        btnProps={{ loading }}
      />
      {!isForgot && (
        <Button
          type="link"
          onClick={resendCode}
          disabled={loading}
          style={{ marginTop: "12px", paddingInline: 0 }}
        >
          Didn't get a code? Send a new one
        </Button>
      )}
      <ToastContainer />
    </div>
  );
};

export default ForgotPassword;
